/* eslint-disable react-hooks/rules-of-hooks */
import { useTranslation } from 'react-i18next';
import { formatter } from '@repo/shared';
import { type ColumnDef } from '@tanstack/react-table';

import { GenerateAudioModelRun } from '@/api/generated.schemas';
import { ColumnHeader } from '@/components/blocks/data-table/column-header';
import { Preview } from '@/components/blocks/preview/preview';

export const columns: Array<ColumnDef<GenerateAudioModelRun>> = [
  {
    accessorKey: 'input',
    header: ({ column }) => {
      const { t } = useTranslation();
      return (
        <ColumnHeader
          column={column}
          title={t('overview.input')}
        />
      );
    },
    cell: ({ row }) => {
      const input = row.original.input;
      if (!input?.path) {
        return null;
      }

      return (
        <Preview
          id={input.id}
          options={{
            mutationKey: `${input.id}`,
            path: input.path,
          }}
        />
      );
    },
  },
  {
    accessorKey: 'sourceLanguage',
    header: ({ column }) => {
      const { t } = useTranslation();
      return (
        <ColumnHeader
          column={column}
          title={t('overview.sourceLanguageCode')}
        />
      );
    },
    cell: ({ row }) => row.original.languageCode,
  },
  {
    accessorKey: 'duration',
    header: ({ column }) => {
      const { t } = useTranslation();
      return (
        <ColumnHeader
          column={column}
          title={t('overview.duration')}
        />
      );
    },
    cell: ({ row }) => formatter.metrics.time(row.original.metrics?.time),
  },
  {
    accessorKey: 'cost',
    header: ({ column }) => {
      const { t } = useTranslation();
      return (
        <ColumnHeader
          column={column}
          title={t('metrics.cost')}
        />
      );
    },
    cell: ({ row }) => formatter.metrics.cost(row.original.metrics?.cost),
  },
  {
    accessorKey: 'latency',
    header: ({ column }) => {
      const { t } = useTranslation();
      return (
        <ColumnHeader
          column={column}
          title={t('metrics.latency')}
        />
      );
    },
    cell: ({ row }) => formatter.metrics.time(row.original.metrics?.time),
  },
  {
    accessorKey: 'wordsPerMinute',
    header: ({ column }) => {
      const { t } = useTranslation();
      return (
        <ColumnHeader
          column={column}
          title={t('metrics.wordsPerMinute')}
        />
      );
    },
    cell: ({ row }) =>
      formatter.metrics.wordsPerMinute(row.original.metrics?.wordsPerMinute),
  },
  {
    accessorKey: 'wordErrorRate',
    header: ({ column }) => {
      const { t } = useTranslation();
      return (
        <ColumnHeader
          column={column}
          title={t('metrics.wordErrorRate')}
        />
      );
    },
    cell: ({ row }) =>
      formatter.metrics.wordErrorRate(row.original.metrics?.wordErrorRate),
  },
  {
    accessorKey: 'output',
    header: ({ column }) => {
      const { t } = useTranslation();
      return (
        <ColumnHeader
          column={column}
          title={t('components.audioPlayer.play')}
        />
      );
    },
    cell: ({ row }) => {
      const { output, error } = row.original;
      if (error) {
        return (
          <span className='text-destructive-foreground'>
            {error.description}
          </span>
        );
      }
      if (output?.path)
        return (
          <Preview
            id={output.id}
            options={{
              mutationKey: `${output.id}`,
              path: output.path,
            }}
          />
        );

      return null;
    },
  },
];
